import {
  BrowserGroupKey,
  BrowserItemKind,
  Design,
  DesignBrowserVisibility,
} from '../types';

const GROUP_DEFAULT_VISIBLE = true;

export function isBrowserGroupVisible(
  design: Design | null | undefined,
  group: BrowserGroupKey
): boolean {
  if (!design) return true;
  const flag = design.browserVisibility?.[group];
  return flag !== false;
}

export function isEquipmentTypeVisible(
  design: Design | null | undefined,
  typeKey: string
): boolean {
  if (!isBrowserGroupVisible(design, 'equipment')) return false;
  const flag = design?.browserVisibility?.equipmentTypes?.[typeKey];
  return flag !== false;
}

export function isItemEffectivelyVisible(
  design: Design | null | undefined,
  kind: BrowserItemKind,
  id: string,
  itemHidden?: boolean
): boolean {
  if (itemHidden) return false;
  if (!design) return true;

  switch (kind) {
    case 'equipment': {
      const eq = design.equipment.find((e) => e.id === id);
      if (!eq) return !itemHidden;
      return isEquipmentTypeVisible(design, eq.equipment.type) && !eq.hidden;
    }
    case 'cable':
      return isBrowserGroupVisible(design, 'cables') && !(design.cables.find((c) => c.id === id)?.hidden);
    case 'rack':
      return isBrowserGroupVisible(design, 'racks') && !(design.racks.find((r) => r.id === id)?.hidden);
    case 'box':
      return isBrowserGroupVisible(design, 'boxes') && !(design.boxes?.find((b) => b.id === id)?.hidden);
    case 'label':
      return isBrowserGroupVisible(design, 'labels') && !(design.labels?.find((l) => l.id === id)?.hidden);
    case 'wall':
      return (
        isBrowserGroupVisible(design, 'walls') &&
        !(design.floorPlan?.walls.find((w) => w.id === id)?.hidden)
      );
    case 'room':
      return (
        isBrowserGroupVisible(design, 'rooms') &&
        !(design.floorPlan?.rooms.find((r) => r.id === id)?.hidden)
      );
    case 'door':
      return (
        isBrowserGroupVisible(design, 'doors') &&
        !(design.floorPlan?.openings.find((o) => o.id === id)?.hidden)
      );
    case 'referenceImage':
      return (
        isBrowserGroupVisible(design, 'referenceImages') &&
        !(design.floorPlan?.referenceImages?.find((r) => r.id === id)?.hidden)
      );
    default:
      return true;
  }
}

/** Quick check when the item object is already in hand. */
export function isVisibleOnCanvas(
  design: Design | null | undefined,
  kind: BrowserItemKind,
  item: { id: string; hidden?: boolean },
  equipmentType?: string
): boolean {
  if (item.hidden) return false;
  if (!design) return true;
  switch (kind) {
    case 'equipment':
      return isEquipmentTypeVisible(design, equipmentType ?? '');
    case 'cable':
      return isBrowserGroupVisible(design, 'cables');
    case 'rack':
      return isBrowserGroupVisible(design, 'racks');
    case 'box':
      return isBrowserGroupVisible(design, 'boxes');
    case 'label':
      return isBrowserGroupVisible(design, 'labels');
    case 'wall':
      return isBrowserGroupVisible(design, 'walls');
    case 'room':
      return isBrowserGroupVisible(design, 'rooms');
    case 'door':
      return isBrowserGroupVisible(design, 'doors');
    case 'referenceImage':
      return isBrowserGroupVisible(design, 'referenceImages');
    default:
      return true;
  }
}

export function withBrowserGroupVisible(
  visibility: DesignBrowserVisibility | undefined,
  group: BrowserGroupKey,
  visible: boolean
): DesignBrowserVisibility {
  return {
    ...(visibility ?? {}),
    [group]: visible,
  };
}

export function withEquipmentTypeVisible(
  visibility: DesignBrowserVisibility | undefined,
  typeKey: string,
  visible: boolean
): DesignBrowserVisibility {
  return {
    ...(visibility ?? {}),
    equipmentTypes: {
      ...(visibility?.equipmentTypes ?? {}),
      [typeKey]: visible,
    },
  };
}

export { GROUP_DEFAULT_VISIBLE };
